import { useTranslation } from 'react-i18next';
import type { NetworkCostPolicyDto } from '@shared/api/contracts/reconciliation';
import { cn } from '@shared/lib/utils';
import { TableCell, TableRow } from '@shared/ui';
import {
  formatPolicyBufferPercent,
  policyAppliesToKind,
  policyAppliesToLabel,
  policyOperationLabel,
  policyPayerLabel,
} from '../lib/network-cost-policy-format';

const APPLIES_TO_CLASS: Record<'global' | 'merchant', string> = {
  global: 'bg-primary/10 text-primary',
  merchant: 'bg-accent text-accent-foreground',
};

function NetworkCostPolicyAppliesToCell({ policy }: { policy: NetworkCostPolicyDto }) {
  const { t } = useTranslation('cabinet');
  const kind = policyAppliesToKind(policy);

  return (
    <TableCell className="min-w-[9rem] sm:min-w-[10rem]">
      <span
        className={cn(
          'inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
          APPLIES_TO_CLASS[kind],
        )}
        title={policy.scopeKey}
      >
        {policyAppliesToLabel(t, policy)}
      </span>
    </TableCell>
  );
}

interface NetworkCostPolicyTableRowProps {
  policy: NetworkCostPolicyDto;
}

export function NetworkCostPolicyTableRow({ policy }: NetworkCostPolicyTableRowProps) {
  const { t } = useTranslation('cabinet');

  return (
    <TableRow>
      <NetworkCostPolicyAppliesToCell policy={policy} />
      <TableCell>{policyOperationLabel(t, policy.operationType)}</TableCell>
      <TableCell>
        <span className="inline-flex rounded-lg border border-border bg-muted/30 px-2 py-0.5 font-mono text-xs">
          {policy.physicalNetwork}
        </span>
      </TableCell>
      <TableCell className="text-sm">{policyPayerLabel(t, policy.payer)}</TableCell>
      <TableCell className="tabular-nums text-sm">{formatPolicyBufferPercent(policy.bufferPercent)}</TableCell>
      <TableCell>
        {policy.enabled ? (
          <span className="text-xs text-muted-foreground">{t('reconciliation.policies.status.active')}</span>
        ) : (
          <span className="inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
            {t('reconciliation.policies.status.disabled')}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}
